import React, { useState } from 'react';
import { ExerciseSelector } from './ExerciseSelector';
import { ExerciseCard } from './ExerciseCard';
import type { Exercise } from '../types';

/**
 * Demo component to showcase ExerciseSelector functionality
 * This component demonstrates both single and multi-selection modes
 */
export const ExerciseSelectorDemo: React.FC = () => {
  const [selectedExercises, setSelectedExercises] = useState<Exercise[]>([]);
  const [singleSelectedExercise, setSingleSelectedExercise] = useState<Exercise | null>(null);
  const [mode, setMode] = useState<'single' | 'multi'>('single');

  const handleExerciseSelect = (exercise: Exercise) => {
    if (mode === 'single') {
      setSingleSelectedExercise(exercise);
    } else {
      setSelectedExercises(prev => {
        const isAlreadySelected = prev.some(ex => ex.id === exercise.id);
        if (isAlreadySelected) {
          // Remove if already selected
          return prev.filter(ex => ex.id !== exercise.id);
        } else {
          // Add if not selected
          return [...prev, exercise];
        }
      });
    }
  };

  const clearSelection = () => {
    setSelectedExercises([]);
    setSingleSelectedExercise(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Выбор упражнений
        </h1>
        <p className="text-gray-600">
          Демонстрация компонентов для выбора упражнений из каталога
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="flex justify-center">
        <div className="bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setMode('single')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'single'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Одиночный выбор
          </button>
          <button
            onClick={() => setMode('multi')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'multi'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Множественный выбор
          </button>
        </div>
      </div>

      {/* Selection Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === 'single' ? 'Выбранное упражнение' : 'Выбранные упражнения'}
          </h3>
          {((mode === 'single' && singleSelectedExercise) || 
            (mode === 'multi' && selectedExercises.length > 0)) && (
            <button
              onClick={clearSelection}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Очистить
            </button>
          )}
        </div>

        {mode === 'single' ? (
          singleSelectedExercise ? (
            <ExerciseCard
              exercise={singleSelectedExercise}
              showDetails={true}
              className="max-w-md"
            />
          ) : (
            <p className="text-gray-500 italic">Упражнение не выбрано</p>
          )
        ) : (
          selectedExercises.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2">
              {selectedExercises.map(exercise => (
                <ExerciseCard
                  key={exercise.id}
                  exercise={exercise}
                  isSelected={true}
                  onSelect={handleExerciseSelect}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic">Упражнения не выбраны</p>
          )
        )}
      </div>

      {/* Exercise Selector */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Каталог упражнений
        </h3>
        <ExerciseSelector
          onExerciseSelect={handleExerciseSelect}
          selectedExercises={mode === 'multi' ? selectedExercises : (singleSelectedExercise ? [singleSelectedExercise] : [])}
          multiSelect={mode === 'multi'}
          showDetails={false}
        />
      </div>
    </div>
  );
};

export default ExerciseSelectorDemo;